import { FC, useEffect, useRef, useState } from "react";
import { useDebounce } from "ui";
import FilterRow from "../components/generic-table/filter-row";
import DashboardContentLayout from "../components/dashboard-content-layout";
import sanitizeFilterValue from "../../../utils/sanitize-filter-value";
import {
  WeatherReportSeverityEnum,
  weatherReportSeverityTitleMap,
  weatherSeverityValues,
} from "service/types/enum/weather-severity-enum";
import SnowReportsTable from "./snow-reports-table";
import { isEmpty as isEmptyValue } from "lodash";
import { Nullable, useAdminWeatherReportControllerGetAll } from "service";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortOptions } from "../components/generic-table/generic-table";
import { AdRoutes } from "../../../constants/ad-routes";
import { Box } from "@mui/material";
import { Add } from "@mui/icons-material";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import HeaderStandardActions from "../../../shared/components/header-items/HeaderStandardActions";
import { useSocketEvents } from "../../../web-sockets/use-socket-events";
import { usePersistedSearchParams } from "../../../hooks/use-persisted-search-params";
import EmptyState, {
  noResultsEmptyState,
} from "@/shared/components/EmptyState";
import { useListEmptyState } from "@/shared/hooks/useListEmptyState";

const weatherSeveritySelectOptions = weatherSeverityValues.map((a) => ({
  label: weatherReportSeverityTitleMap[a],
  value: a,
}));

const SnowReportsPage: FC = () => {
  const [params, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(
    params.get("page") ? parseInt(params.get("page") as string) : 1,
  );
  const [search, setSearch] = useState<string>(params.get("search") || "");
  const [severity, setSeverity] = useState<
    WeatherReportSeverityEnum | undefined
  >(
    params.get("severity")
      ? (params.get("severity") as WeatherReportSeverityEnum)
      : undefined,
  );
  const [sortOptions, setSortOptions] = useState<Nullable<SortOptions>>(
    params.get("sortBy")
      ? {
          sortBy: params.get("sortBy") || "createdAt",
          direction: (params.get("direction") || "desc") as "asc" | "desc",
        }
      : null,
  );
  const { debounce } = useDebounce(475);
  const [snowAmount] = useState<number | undefined>();
  const [limit, setLimit] = useState<number>(
    params.get("limit") ? parseInt(params.get("limit") as string) : 50,
  );

  // Persist search params for breadcrumb navigation
  usePersistedSearchParams();

  // Track previous values to avoid unnecessary URL updates on mount
  const prevPageRef = useRef(page);
  const prevLimitRef = useRef(limit);

  // Sync page and limit changes to URL (only when they actually change)
  useEffect(() => {
    // Skip if values haven't changed from their previous state
    if (prevPageRef.current === page && prevLimitRef.current === limit) {
      return;
    }
    prevPageRef.current = page;
    prevLimitRef.current = limit;

    setSearchParams((prev) => {
      if (page > 1) {
        prev.set("page", page.toString());
      } else {
        prev.delete("page");
      }
      if (limit !== 50) {
        prev.set("limit", limit.toString());
      } else {
        prev.delete("limit");
      }
      return prev;
    });
  }, [page, limit, setSearchParams]);

  const { isLoading, isError, refetch, data } =
    useAdminWeatherReportControllerGetAll({
      queryParams: {
        limit,
        page,
        severity,
        snowAmount,
        search: isEmptyValue(search) ? undefined : search,
        sort: sortOptions?.sortBy as
          | "snowAmount"
          | "createdAt"
          | "updatedAt"
          | "address"
          | "elevation"
          | "reporter",
        order: sortOptions?.direction,
      },
    });

  // Connect to WebSocket to listen for weather report events
  useSocketEvents({
    currentPage: "snow-reports",
  });

  const onSearch = (searchValue: string) => {
    debounce(() => {
      setSearch(searchValue);
      // Reset to page 1 when search changes
      setPage(1);
      // Note: FilterRow already updates the 'search' param in the URL
    });
  };

  const hasActiveFilters = Boolean(search) || Boolean(severity);
  const { isEmpty, isTrueEmpty } = useListEmptyState(
    isLoading,
    data?.metaData.count ?? 0,
    hasActiveFilters,
    isError,
  );

  const clearFilters = () => {
    setSearch("");
    setSeverity(undefined);
    setPage(1);
    setSearchParams((prev) => {
      prev.delete("search");
      prev.delete("severity");
      prev.delete("page");
      return prev;
    });
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Snow Reports",
                url: AdRoutes.snowReports,
              },
            ]}
          />
          <HeaderItemsRight>
            <HeaderStandardActions
              onRefresh={isTrueEmpty ? undefined : refetch}
              onAdd={() => navigate(AdRoutes.snowReportsCreate)}
              addButtonText="Create Snow Report"
            />
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      {!isTrueEmpty && (
        <FilterRow
          tablePaginationProps={{
            limit,
            page,
            isLoading: isLoading,
            count: data?.metaData.count || 10,
            setToPage: setPage,
            setLimit,
          }}
          search={search}
          onSearchChanged={onSearch}
          filters={[
            {
              placeholder: "Weather Condition",
              options: weatherSeveritySelectOptions,
              value: severity,
              onChange: (value: string) => {
                const v = sanitizeFilterValue(value, weatherSeverityValues);
                setSeverity(v);
                setSearchParams((params) => {
                  v ? params.set("severity", v) : params.delete("severity");
                  return params;
                });
              },
            },
          ]}
        />
      )}
      {isTrueEmpty ? (
        <Box mt="20px">
          <EmptyState
            title="No Snow Reports Yet"
            description="New snow reports will appear here as they are added"
            actionLabel="Create Snow Report"
            actionStartIcon={<Add />}
            onAction={() => navigate(AdRoutes.snowReportsCreate)}
          />
        </Box>
      ) : isEmpty ? (
        <Box mt="20px">
          <EmptyState {...noResultsEmptyState("snow reports", clearFilters)} />
        </Box>
      ) : (
        <SnowReportsTable
          data={data?.data}
          isLoading={isLoading}
          sortOptions={sortOptions}
          sortOptionsChange={setSortOptions}
        />
      )}
    </DashboardContentLayout>
  );
};

export default SnowReportsPage;
